"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Maximize2, SlidersHorizontal as Sliders, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const venueFeatures = [{
  title: "Controller & Operation",
  desc: "Review the controller and documented operating functions for the exact machine.",
  icon: Sliders
}, {
  title: "Space & Setup",
  desc: "Compare the full operating footprint, power requirements and access needs for the exact equipment package.",
  icon: Maximize2
}, {
  title: "Parts & Ownership",
  desc: "Consider replacement-parts availability and the product information available for long-term ownership.",
  icon: Wrench
}];
export default function CommercialMachines() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="commercial-mechanical-bull-riding-machines" data-nav="light" className="relative w-full border-t border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Industrial Framed Photography */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-6">
            <div className="relative border border-secondary bg-muted p-2 sm:p-3">
              {/* Machined Datum Marks */}
              <span aria-hidden="true" className="absolute -top-1.5 -left-1.5 h-3 w-3 border-t-2 border-l-2 border-foreground" />
              <span aria-hidden="true" className="absolute -top-1.5 -right-1.5 h-3 w-3 border-t-2 border-r-2 border-foreground" />
              <span aria-hidden="true" className="absolute -bottom-1.5 -left-1.5 h-3 w-3 border-b-2 border-l-2 border-foreground" />
              <span aria-hidden="true" className="absolute -bottom-1.5 -right-1.5 h-3 w-3 border-b-2 border-r-2 border-foreground" />

              {/* Main Machine Visual */}
              <div className="relative aspect-16/10 w-full overflow-hidden border border-border bg-secondary">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_bull_riding_venue.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_bull_riding_venue.png-wvc-sizes" alt="Commercial mechanical bull operating inside an entertainment venue with protective landing mattress" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" src="https://mechanicalbullshop.com/wp-content/uploads/2026/09/4b30f4f0-7983-4963-9f4d-d7703e44c6bd.png" />
              </div>
            </div>
          </motion.div>

          {/* Right Column: Engineering Copy & Venue Requirements */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="flex flex-col lg:col-span-6">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-primary" aria-hidden="true" />
              <p className="font-mono text-xs tracking-wider text-primary uppercase">
                Industrial Venue Solutions
              </p>
            </div>

            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl lg:text-5xl">
              Commercial Mechanical Bull Riding Machines
            </h2>

            <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground sm:text-lg">
              <p>
                A commercial bull riding machine needs to fit the way you intend to use it.
              </p>

              <p>
                A machine that stays permanently inside one venue creates different priorities from one that an event operator loads, transports and sets up repeatedly. Likewise, a buyer running frequent events may care more about parts availability and downtime than a buyer using the equipment occasionally.
              </p>

              <p>
                Before choosing a mechanical bull riding machine, look beyond appearance and compare the system itself.
              </p>

              <div>
                <p className="font-medium text-foreground">
                  Important questions include:
                </p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5 text-foreground marker:text-primary">
                  <li>What type of controller does it use?</li>
                  <li>Which movements and operating modes does it provide?</li>
                  <li>What safety area comes with it?</li>
                  <li>How much floor space does the complete setup require?</li>
                  <li>What electrical supply does the bull and blower need?</li>
                  <li>What is included in the advertised price?</li>
                  <li>Are replacement parts available?</li>
                  <li>How is the machine packed and transported?</li>
                  <li>What support is available after the purchase?</li>
                </ul>
              </div>

              <p>
                These details often matter more over the life of the machine than cosmetic differences between two bulls.
              </p>

              <p>
                If you are comparing a mechanical bull machine for sale for a bar, event operation, entertainment center or similar business, start with the actual workload and venue rather than simply choosing the cheapest package.
              </p>
            </div>

            {/* Technical Specification Bullets */}
            <div className="mt-8 space-y-4">
              {venueFeatures.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.title} data-index={i} className="border border-border bg-card p-4 transition-colors hover:border-foreground">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0 bg-muted p-1.5 text-primary">
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg font-semibold tracking-wide text-foreground uppercase">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            {/* Primary Action Button */}
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-12 rounded-lg border border-primary bg-primary px-8 font-serif text-base font-semibold tracking-wider text-primary-foreground uppercase shadow-none transition-colors hover:bg-primary/90">
                <Link to="/NewMechanicalBullsPage" className="inline-flex items-center gap-2">
                  <span>Shop Mechanical Bulls</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 rounded-lg border border-foreground bg-transparent px-6 font-serif text-base font-semibold tracking-wider text-foreground uppercase shadow-none transition-colors hover:bg-foreground hover:text-background">
                <Link to="/SetupSafetyAndMaintenancePage">
                  View Safety & Setup
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}